import type {GoogleOneTapSettings} from '@common/auth/one-tap/one-tap-rules';
import {ImapConnectionCredentials} from '@common/admin/settings/pages/email-settings/incoming-email/imap-connection-credentials';
import {MenuConfig, MenuItemConfig} from '@common/menus/menu-config';
import {SectionConfig} from '@common/ui/landing-page/landing-page-config';
import {Settings} from '@ui/settings/settings';

export const DEFAULT_CHUNK_SIZE = 2097152; // 2MB
export const DEFAULT_PARALLEL_FILES = 3;
export const DEFAULT_PARALLEL_PARTS = 4;
export const DEFAULT_PART_SIZE = 20971520; // 20 MB

export type CaptchaAction = keyof NonNullable<
  Required<Settings>['captcha']['enable']
>;

export type UploadingBackendSettings = {
  id: string;
  name: string;
  root?: string;
  domain?: string;
  type: string;
  capacity_bytes?: number | null;
  config?: Record<string, string | number | boolean>;
};

export type UploadingTypeSettings = {
  backends: string[];
  root?: string;
  max_file_size?: number;
  accept?: string[];
};

export interface BaseBackendSettings {
  version: string;
  branding: {
    logo_light: string;
    logo_dark: string;
    logo_light_mobile: string;
    logo_dark_mobile: string;
    /** Optional RTL wordmark for light theme (dark bg). Falls back to logo_light. */
    logo_light_rtl?: string;
    /** Optional RTL wordmark for dark theme (light bg). Falls back to logo_dark. */
    logo_dark_rtl?: string;
    logo_light_mobile_rtl?: string;
    logo_dark_mobile_rtl?: string;
    site_name: string;
    site_description: string;
    favicon: string;
  };
  landingPage?: {
    sections?: SectionConfig[];
    baseLocale?: string;
  };
  locale?: {
    default?: string;
  };
  menus: MenuConfig[];
  html_base_uri: string;
  cookie_notice?: {
    enable: boolean;
    position: 'top' | 'bottom';
    button?: MenuItemConfig;
  };
  logging: {
    sentry_public?: string;
    email_log_keep?:
      | '1_hour'
      | '1_day'
      | '1_week'
      | '1_month'
      | '3_months'
      | '1_year'
      | 'never';
    schedule_log_keep?:
      | '1_hour'
      | '1_day'
      | '1_week'
      | '1_month'
      | '3_months'
      | '1_year'
      | 'never';
    error_log_keep?:
      | '1_hour'
      | '1_day'
      | '1_week'
      | '1_month'
      | '3_months'
      | '1_year'
      | 'never';
  };
  themes?: {
    default_scheme?: 'dark' | 'light' | 'system';
    user_change: boolean;
  };
  custom_domains?: {
    default_host?: string;
    allow_select?: boolean;
    allow_all_option?: boolean;
  };
  i18n: {
    enable: boolean;
    default_localization: string;
  };
  api?: {
    integrated: boolean;
  };
  websockets?: {
    integrated: boolean;
  };
  incoming_email?: {
    integrated?: boolean;
    imap?: {
      connections?: ImapConnectionCredentials[];
    };
    mailgun?: {
      enabled?: boolean;
      verify?: boolean;
    };
    gmail?: {
      enabled?: boolean;
      topicName?: string;
    };
    pipe?: {
      enabled?: boolean;
    };
    api?: {
      enabled?: boolean;
    };
  };
  billing?: {
    integrated?: boolean;
    enable?: boolean;
    trial_days?: number;
    trial_shared_bytes?: number;
    paypal_test_mode?: boolean;
    stripe_public_key?: string;
    invoice?: {
      address?: string;
      notes?: string;
    };
    paypal?: {
      public_key?: string;
      enable?: boolean;
    };
    stripe?: {
      enable?: boolean;
    };
  };
  notifications: {
    integrated: boolean;
  };
  notif: {
    subs: {
      integrated: boolean;
    };
  };
  site: {
    hide_docs_buttons: boolean;
    has_mobile_app: boolean;
    demo: boolean;
  };
  registration?: {
    disable?: boolean;
    policies?: MenuItemConfig[];
  };
  social?: {
    google?: {
      enable: boolean;
      one_tap?: GoogleOneTapSettings;
    };
    twitter?: {
      enable: boolean;
    };
    facebook?: {
      enable: boolean;
    };
    compact_buttons: boolean;
    requireAccount?: boolean;
  };
  auth?: {
    domain_blacklist?: string;
  };
  workspaces: {
    integrated: boolean;
  };
  uploading?: {
    chunk_size?: number;
    // Build 128: files uploaded at the same time (1–6, default 3).
    parallel_files?: number;
    // Build 128: S3 multipart parts sent at the same time per file (1–10, default 4).
    parallel_parts?: number;
    // Build 128: S3 multipart part size in bytes (5 MB–512 MB, default 20 MB).
    part_size?: number;
    backends?: UploadingBackendSettings[];
    disable_tus?: boolean;
    types?: Record<string, UploadingTypeSettings>;
  };
  require_email_confirmation: boolean;
  email_verification?: {
    code_expiry_minutes?: number | string;
    resend_cooldown_seconds?: number | string;
    max_attempts?: number | string;
    reminder?: {enable?: boolean | string; after_hours?: number | string};
    cleanup?: {enable?: boolean | string; after_days?: number | string};
  };
  single_device_login: boolean;
  mail?: {
    contact_page_address?: string;
    handler?: string;
  };
  captcha?: {
    enable?: Record<'contact' | 'register', boolean>;
    provider?: 'recaptcha' | 'turnstile';
    g_site_key?: string;
    g_secret_key?: string;
    t_site_key?: string;
    t_secret_key?: string;
  };
  broadcasting?: {
    driver?: 'pusher' | 'reverb' | 'ably' | 'null' | 'log';
    key?: string;
    cluster?: string;
    host?: string;
    port?: number;
    scheme?: string;
  };
  analytics?: {
    tracking_code?: string;
    gchart_api_key?: string;
  };
}
